import { analyzeCashFlow, getCategoryDisplayName, CF_CATEGORIES } from './freee-mapping.js';
import { FISCAL_YEAR_CONFIG } from './config.js';

/**
 * キャッシュフロー計算エンジン
 */
export class CashFlowEngine {
  constructor() {
    this.transactions = [];
  }

  /**
   * Load transactions data
   * @param {Array} transactions - Transaction records
   */
  loadTransactions(transactions) {
    this.transactions = transactions;
  }

  /**
   * Calculate fiscal year from date
   * @param {Date} date - Date to check
   * @returns {number} - Fiscal year (e.g., 2024)
   */
  getFiscalYear(date) {
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // JavaScript months are 0-indexed
    
    // If month is before May, it's previous fiscal year
    if (month < FISCAL_YEAR_CONFIG.START_MONTH) {
      return year - 1;
    }
    return year;
  }

  /**
   * Get fiscal year date range
   * @param {number} fiscalYear - Fiscal year
   * @returns {Object} - {startDate, endDate}
   */
  getFiscalYearRange(fiscalYear) {
    const startDate = new Date(fiscalYear, FISCAL_YEAR_CONFIG.START_MONTH - 1, 1);
    const endDate = new Date(fiscalYear + 1, FISCAL_YEAR_CONFIG.END_MONTH, 0, 23, 59, 59, 999);
    
    return { startDate, endDate };
  }

  /**
   * Get month date range
   * @param {number} year - Year
   * @param {number} month - Month (1-12)
   * @returns {Object} - {startDate, endDate}
   */
  getMonthRange(year, month) {
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0, 23, 59, 59, 999);
    
    return { startDate, endDate };
  }

  /**
   * Filter transactions by date range
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Array} - Filtered transactions
   */
  filterByDateRange(startDate, endDate) {
    return this.transactions.filter(transaction => {
      const transactionDate = new Date(transaction.transaction_date);
      return transactionDate >= startDate && transactionDate <= endDate;
    });
  }

  /**
   * Calculate cashflow summary for transactions
   * @param {Array} transactions - Transactions to analyze
   * @returns {Object} - Cashflow summary by category
   */
  calculateCashFlow(transactions) {
    const summary = {
      [CF_CATEGORIES.OPERATING]: { inflow: 0, outflow: 0, net: 0, transactions: [] },
      [CF_CATEGORIES.INVESTING]: { inflow: 0, outflow: 0, net: 0, transactions: [] },
      [CF_CATEGORIES.FINANCING]: { inflow: 0, outflow: 0, net: 0, transactions: [] }
    };

    transactions.forEach(transaction => {
      const cashFlow = analyzeCashFlow(transaction);
      
      if (cashFlow) {
        const category = cashFlow.category;
        const amount = cashFlow.amount;
        
        if (cashFlow.type === 'inflow') {
          summary[category].inflow += amount;
        } else {
          summary[category].outflow += amount;
        }
        
        summary[category].transactions.push({
          ...transaction,
          cashflow_type: cashFlow.type,
          cashflow_amount: amount,
          cashflow_account: cashFlow.account
        });
      }
    });

    // Calculate net cashflow for each category
    Object.keys(summary).forEach(category => {
      summary[category].net = summary[category].inflow - summary[category].outflow;
    });

    // Add total summary
    const total = {
      inflow: summary[CF_CATEGORIES.OPERATING].inflow + 
              summary[CF_CATEGORIES.INVESTING].inflow + 
              summary[CF_CATEGORIES.FINANCING].inflow,
      outflow: summary[CF_CATEGORIES.OPERATING].outflow + 
               summary[CF_CATEGORIES.INVESTING].outflow + 
               summary[CF_CATEGORIES.FINANCING].outflow,
      net: 0
    };
    total.net = total.inflow - total.outflow;
    summary.total = total;

    return summary;
  }

  /**
   * Get cashflow summary by fiscal year
   * @param {number} fiscalYear - Fiscal year
   * @returns {Object} - Cashflow summary
   */
  getCashFlowByFiscalYear(fiscalYear) {
    const { startDate, endDate } = this.getFiscalYearRange(fiscalYear);
    const transactions = this.filterByDateRange(startDate, endDate);
    return this.calculateCashFlow(transactions);
  }

  /**
   * Get cashflow summary by month
   * @param {number} year - Year
   * @param {number} month - Month (1-12)
   * @returns {Object} - Cashflow summary
   */
  getCashFlowByMonth(year, month) {
    const { startDate, endDate } = this.getMonthRange(year, month);
    const transactions = this.filterByDateRange(startDate, endDate);
    return this.calculateCashFlow(transactions);
  }

  /**
   * Get monthly cashflow for fiscal year
   * @param {number} fiscalYear - Fiscal year
   * @returns {Array} - Monthly cashflow summaries
   */
  getMonthlyBreakdown(fiscalYear) {
    const monthlySummaries = [];
    
    // Fiscal year: May to April
    for (let i = 0; i < 12; i++) {
      let year = fiscalYear;
      let month = FISCAL_YEAR_CONFIG.START_MONTH + i;
      
      if (month > 12) {
        month -= 12;
        year += 1;
      }
      
      const summary = this.getCashFlowByMonth(year, month);
      monthlySummaries.push({
        year,
        month,
        label: `${year}年${month}月`,
        ...summary
      });
    }
    
    return monthlySummaries;
  }

  /**
   * Get current fiscal year
   * @returns {number} - Current fiscal year
   */
  getCurrentFiscalYear() {
    return this.getFiscalYear(new Date());
  }

  /**
   * Get available fiscal years from transactions
   * @returns {Array} - Array of fiscal years
   */
  getAvailableFiscalYears() {
    const years = new Set();
    
    this.transactions.forEach(transaction => {
      const date = new Date(transaction.transaction_date);
      const fiscalYear = this.getFiscalYear(date);
      years.add(fiscalYear);
    });
    
    return Array.from(years).sort((a, b) => b - a);
  }
}

// Export singleton instance
export const cashFlowEngine = new CashFlowEngine();
